import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AuthStateService } from './auth-state.service';
import {
  AuthMethods,
  AuthStatusResponse,
  LoginRequest,
  LoginResponse,
  MicrosoftStartResponse,
  Tenant,
  User
} from './auth.models';
import {
  AUTH_LIMITS,
  isSafeMicrosoftAuthorizeUrl,
  isValidEmail,
  isValidInviteToken,
  isValidMfaCode,
  normalizeEmail,
  parseTenantId,
  passwordIssues,
  sanitizeInviteToken,
  sanitizeMfaCode,
  sanitizeUserMessage
} from './auth.validators';
import { extractUserFacingError } from '../http/app-http-error';
import { LoadingService } from '../services/loading.service';
import { environment } from '../../../environments/environment';

const API_UNREACHABLE_MESSAGE =
  'Cannot reach the OneDesk API. Check that the backend is running and try again.';

function looksLikeUnreachableAuthError(message: string | null | undefined): boolean {
  if (!message) {
    return false;
  }
  return /cannot reach|onedesk|onedesk api|backend is running|network error|unavailable/i.test(
    message
  );
}

interface PendingCredentials {
  email: string;
  password: string;
  tenant_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authState = inject(AuthStateService);
  private readonly loading = inject(LoadingService);
  private readonly baseUrl = environment.apiBaseUrl;
  private pendingCredentials?: PendingCredentials;
  private pendingOtpEmail?: string;
  private lastDevCode?: string;
  private inFlight = false;
  private failedAttempts = 0;
  private lockoutUntil = 0;

  private readonly lockoutRemainingSignal = signal(0);
  readonly lockoutRemaining = this.lockoutRemainingSignal.asReadonly();
  readonly isLockedOut = computed(() => this.lockoutRemainingSignal() > 0);

  /**
   * Irish Taylor staff — full-page navigation to Microsoft.
   * Frontend never sees a Microsoft token. Backend sets the session cookie.
   * Pass `inviteToken` when activating a staff invitation.
   */
  startMicrosoftLogin(inviteToken?: string): Observable<void> {
    if (this.inFlight || this.authState.isLoading()) {
      return throwError(() => new Error('Microsoft sign-in is already in progress.'));
    }

    this.authState.setLoading(true);
    this.authState.setError(null);
    this.inFlight = true;
    this.loading.show('Connecting to Microsoft…');

    const safeInviteToken =
      typeof inviteToken === 'string' && inviteToken.trim()
        ? sanitizeInviteToken(inviteToken.trim())
        : '';
    const options = safeInviteToken
      ? { params: { invite_token: safeInviteToken } }
      : {};

    let redirected = false;
    return this.http.get<MicrosoftStartResponse>(`${this.baseUrl}/auth/microsoft/start`, options).pipe(
      map((res) => {
        if (!isSafeMicrosoftAuthorizeUrl(res?.authorize_url)) {
          throw { error: { detail: 'Microsoft sign-in is not available right now.' } };
        }
        return res.authorize_url;
      }),
      tap((authorizeUrl) => {
        redirected = true;
        this.navigateExternal(authorizeUrl);
      }),
      map(() => undefined as void),
      catchError((err) => {
        this.authState.setError(
          this.extractErrorMessage(err, 'Unable to start Microsoft sign-in. Please try again.')
        );
        return throwError(() => err);
      }),
      finalize(() => {
        this.inFlight = false;
        // Keep the overlay up after redirect — the browser is navigating away.
        if (!redirected) {
          this.authState.setLoading(false);
          this.loading.hide();
        }
      })
    );
  }

  /** SmartFreight clients — email + password, then MFA if required. */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    if (this.isCurrentlyLockedOut()) {
      this.authState.setError(this.lockoutMessage());
      return throwError(() => new Error(this.lockoutMessage()));
    }

    const email = normalizeEmail(credentials.email);
    if (!isValidEmail(email)) {
      this.authState.setError('Please enter a valid email address.');
      return throwError(() => new Error('Invalid email'));
    }

    if (passwordIssues(credentials.password).length > 0) {
      this.authState.setError('Please enter a valid password.');
      return throwError(() => new Error('Invalid password'));
    }

    const allowedTenantIds = this.authState.state().tenants.map((t) => t.id);
    const tenantId = parseTenantId(credentials.tenant_id, allowedTenantIds);

    let mfaCode: string | undefined;
    if (credentials.mfa_code) {
      mfaCode = sanitizeMfaCode(credentials.mfa_code);
      if (!isValidMfaCode(mfaCode)) {
        this.authState.setError('Please enter the full 6-digit security code.');
        return throwError(() => new Error('Invalid MFA code'));
      }
    }

    if (this.inFlight) {
      return throwError(() => new Error('Sign-in already in progress'));
    }

    this.inFlight = true;
    this.authState.setLoading(true);
    this.authState.setError(null);
    this.loading.show(mfaCode ? 'Verifying security code…' : 'Signing in…');

    const payload: Record<string, unknown> = { email, password: credentials.password };
    if (mfaCode) {
      payload['mfa_code'] = mfaCode;
    }
    if (tenantId != null) {
      payload['tenant_id'] = tenantId;
    }

    if (!mfaCode) {
      this.wipePendingCredentials();
      this.pendingCredentials = {
        email,
        password: credentials.password,
        tenant_id: tenantId
      };
    }

    return this.http.post<unknown>(`${this.baseUrl}/auth/login`, payload).pipe(
      switchMap(() => this.refreshSessionFromStatus()),
      map((user) => {
        this.inFlight = false;
        this.resetLockout();
        this.wipePendingCredentials();
        return { status: 'SUCCESS', user } as LoginResponse;
      }),
      catchError((err) => {
        this.inFlight = false;
        return this.handleLoginError(err, email, mfaCode ? 'MFA' : 'PASSWORD');
      }),
      finalize(() => this.loading.hide())
    );
  }

  /** Step 2 — same POST /api/auth/login, now including the 6-digit code. */
  verifyMfaCode(code: string, tenant_id?: number): Observable<boolean> {
    if (!this.pendingCredentials) {
      this.authState.setError('Your sign-in session expired. Please start again.');
      return throwError(() => new Error('MFA session expired'));
    }

    const sanitizedCode = sanitizeMfaCode(code);
    if (!isValidMfaCode(sanitizedCode)) {
      this.authState.setError('Please enter the full 6-digit security code.');
      return throwError(() => new Error('Invalid MFA code'));
    }

    const allowedTenantIds = this.authState.state().tenants.map((t) => t.id);
    const tenantId = parseTenantId(tenant_id, allowedTenantIds) ?? this.pendingCredentials.tenant_id;

    return this.login({
      email: this.pendingCredentials.email,
      password: this.pendingCredentials.password,
      mfa_code: sanitizedCode,
      tenant_id: tenantId
    }).pipe(
      switchMap((res) => {
        if (res.status === 'SUCCESS') {
          return of(true);
        }
        if (res.status === 'TENANT_REQUIRED') {
          return throwError(() => ({ type: 'TENANT_REQUIRED' }));
        }
        return throwError(() => new Error('Verification failed. Please try again.'));
      })
    );
  }

  /** Email OTP flow — request a sign-in code for passwordless users. */
  requestOtp(email: string): Observable<{ dev_code?: string }> {
    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized)) {
      this.authState.setError('Please enter a valid email address.');
      return throwError(() => new Error('Invalid email'));
    }

    this.authState.setLoading(true);
    this.authState.setError(null);
    this.loading.show('Sending sign-in code…');

    return this.http
      .post<{ sent: boolean; dev_code?: string; dev_warning?: string }>(
        `${this.baseUrl}/auth/otp/request`,
        { email: normalized }
      )
      .pipe(
        tap((res) => {
          this.authState.setLoading(false);
          this.pendingOtpEmail = normalized;
          this.lastDevCode = undefined;
          if (res.dev_code) {
            this.lastDevCode = res.dev_code;
          }
        }),
        map((res) => ({ dev_code: res.dev_code })),
        catchError((err) => {
          this.authState.setLoading(false);
          this.authState.setError(
            this.extractErrorMessage(err, 'Unable to send sign-in code. Please try again.')
          );
          return throwError(() => err);
        }),
        finalize(() => this.loading.hide())
      );
  }

  /**
   * Email OTP — verify once with { email, code }.
   * Single-org users get a session.
   * Multi-org accounts cannot finish on this path: there is no password for /auth/login,
   * and calling /auth/otp/verify a second time with tenant_id is rejected in practice.
   * In that case we stop and ask them to use email+password with the org picker.
   */
  verifyOtpCode(code: string): Observable<boolean> {
    const email = this.pendingOtpEmail;
    if (!email) {
      this.authState.setError('Your sign-in session expired. Please start again.');
      return throwError(() => new Error('OTP session expired'));
    }

    const sanitizedCode = sanitizeMfaCode(code);
    if (!isValidMfaCode(sanitizedCode)) {
      this.authState.setError('Please enter the full 6-digit security code.');
      return throwError(() => new Error('Invalid code'));
    }

    this.authState.setLoading(true);
    this.authState.setError(null);

    return this.http
      .post<unknown>(`${this.baseUrl}/auth/otp/verify`, { email, code: sanitizedCode })
      .pipe(
        switchMap(() => this.refreshSessionFromStatus()),
        map(() => {
          this.authState.setLoading(false);
          this.clearOtpSession();
          return true;
        }),
        catchError((err) => {
          this.authState.setLoading(false);
          if (this.isTenantRequiredError(err)) {
            // Email OTP cannot complete multi-org selection without a second verify
            // (which fails) or /auth/login (which needs a password). Block this path.
            this.authState.setTenants([]);
            this.authState.setError(
              'This account belongs to more than one organisation. ' +
                'Email code sign-in and Microsoft sign-in only work for a single organisation. ' +
                'Please sign in with your email and password, then choose your organisation.'
            );
            return throwError(() => ({ type: 'OTP_MULTI_TENANT_UNSUPPORTED' }));
          }
          this.authState.setError(
            this.extractErrorMessage(err, 'That code is not valid. Please request a new one.')
          );
          return throwError(() => err);
        })
      );
  }

  getLastDevCode(): string | undefined {
    return this.lastDevCode;
  }

  getPendingOtpEmail(): string | undefined {
    return this.pendingOtpEmail;
  }

  hasPendingOtpSession(): boolean {
    return !!this.pendingOtpEmail;
  }

  hasPendingMfaSession(): boolean {
    return !!this.pendingCredentials?.email && !!this.pendingCredentials?.password;
  }

  logout(): Observable<boolean> {
    this.authState.setLoading(true);
    this.wipePendingCredentials();
    this.resetLockout();
    this.loading.show('Signing out…');
    return this.http.post(`${this.baseUrl}/auth/logout`, {}).pipe(
      tap(() => this.authState.clear()),
      map(() => true),
      catchError(() => {
        this.authState.clear();
        return of(true);
      }),
      finalize(() => this.loading.hide())
    );
  }

  getInviteDetails(token: string): Observable<{
    email: string;
    tenant: string;
    role: string;
    auth_mode: 'password' | 'microsoft';
    mfa_secret?: string;
    mfa_uri?: string;
  }> {
    const safeToken = sanitizeInviteToken(token);
    if (!isValidInviteToken(safeToken)) {
      return throwError(() => ({
        error: { detail: 'This invitation link is invalid, expired, or has already been used.' }
      }));
    }

    return this.http
      .get<{
        email: string;
        tenant: string;
        role: string;
        auth_mode?: string;
        mfa_secret?: string;
        mfa_uri?: string;
      }>(`${this.baseUrl}/auth/invites/${encodeURIComponent(safeToken)}`)
      .pipe(
        map((res) => {
          const mode = res.auth_mode === 'microsoft' ? 'microsoft' : 'password';
          return {
            email: normalizeEmail(res.email),
            tenant: typeof res.tenant === 'string' ? res.tenant.trim().slice(0, 120) : '',
            role: typeof res.role === 'string' ? res.role.trim().slice(0, 40) : '',
            auth_mode: mode as 'password' | 'microsoft',
            mfa_secret:
              mode === 'password' &&
              typeof res.mfa_secret === 'string' &&
              res.mfa_secret.trim()
                ? res.mfa_secret.trim()
                : undefined,
            mfa_uri:
              mode === 'password' &&
              typeof res.mfa_uri === 'string' &&
              res.mfa_uri.trim().startsWith('otpauth://')
                ? res.mfa_uri.trim()
                : undefined
          };
        })
      );
  }

  acceptInvite(
    token: string,
    credentials: { password: string; mfa_code: string }
  ): Observable<User> {
    const safeToken = sanitizeInviteToken(token);
    if (!isValidInviteToken(safeToken)) {
      return throwError(() => ({
        error: { detail: 'This invitation link is invalid, expired, or has already been used.' }
      }));
    }

    if (passwordIssues(credentials.password).length > 0) {
      return throwError(() => ({
        error: {
          detail:
            'Password needs at least 12 characters, at least one letter and at least one digit.'
        }
      }));
    }

    const mfaCode = sanitizeMfaCode(credentials.mfa_code);
    if (!isValidMfaCode(mfaCode)) {
      return throwError(() => ({
        error: {
          detail: 'Please enter the full 6-digit security code from your authenticator app.'
        }
      }));
    }

    this.authState.setLoading(true);
    this.authState.setError(null);

    return this.http
      .post<unknown>(`${this.baseUrl}/auth/invites/${encodeURIComponent(safeToken)}/accept`, {
        password: credentials.password,
        mfa_code: mfaCode
      })
      .pipe(
        switchMap(() => this.refreshSessionFromStatus()),
        tap(() => this.authState.setLoading(false)),
        catchError((err) => {
          this.authState.setLoading(false);
          return throwError(() => err);
        })
      );
  }

  checkAuthStatus(): Observable<boolean> {
    return this.http.get<AuthStatusResponse>(`${this.baseUrl}/auth/status`).pipe(
      switchMap((status) => {
        // Recovered from an earlier unreachable bootstrap.
        if (looksLikeUnreachableAuthError(this.authState.error())) {
          this.authState.setError(null);
        }
        return this.applyAuthStatus(status);
      }),
      catchError((err) => {
        this.wipePendingCredentials();
        this.authState.clear();
        // Status failed (often backend down). Still load methods so login is not a blank card,
        // and surface a clear unreachable message for the login page to show.
        this.authState.setError(extractUserFacingError(err, API_UNREACHABLE_MESSAGE));
        return this.loadAuthMethods().pipe(map(() => false));
      })
    );
  }

  /** Re-run status + methods bootstrap (login "Try again" when API was down). */
  retryAuthBootstrap(): Observable<boolean> {
    this.authState.setError(null);
    this.authState.setLoading(true);
    return this.checkAuthStatus().pipe(
      tap(() => this.authState.setLoading(false)),
      catchError((err) => {
        this.authState.setLoading(false);
        this.authState.setError(extractUserFacingError(err, API_UNREACHABLE_MESSAGE));
        return of(false);
      })
    );
  }

  clearPendingSession(): void {
    this.wipePendingCredentials();
    this.clearOtpSession();
  }

  private clearOtpSession(): void {
    this.pendingOtpEmail = undefined;
    this.lastDevCode = undefined;
  }

  private applyAuthStatus(status: AuthStatusResponse): Observable<boolean> {
    if (status.multi_tenant === false) {
      this.authState.setAuthenticated({
        id: 'v1_bypass',
        email: 'v1@local.com',
        name: 'Local User',
        tenant: 'Local Tenant',
        role: 'publisher'
      });
      return of(true);
    }

    if (status.authenticated === true && this.isValidSessionUser(status)) {
      this.authState.setAuthenticated(this.mapStatusToUser(status));
      return this.loadAuthMethods().pipe(map(() => true));
    }

    this.authState.clear();
    return this.loadAuthMethods().pipe(map(() => false));
  }

  private loadAuthMethods(): Observable<AuthMethods> {
    return this.http.get<AuthMethods>(`${this.baseUrl}/auth/methods`).pipe(
      tap((methods) => this.authState.setMethods(this.sanitizeMethods(methods))),
      catchError(() => {
        // Soft fallback so the password form can still render; login shows the
        // unreachable banner separately when status/methods both failed.
        const fallback: AuthMethods = {
          email_otp: false,
          password: true,
          microsoft: false,
          email_delivery_configured: false,
          dev_show_otp: false
        };
        this.authState.setMethods(fallback);
        return of(fallback);
      })
    );
  }

  private refreshSessionFromStatus(): Observable<User> {
    return this.http.get<AuthStatusResponse>(`${this.baseUrl}/auth/status`).pipe(
      map((status) => {
        if (status.authenticated !== true || !this.isValidSessionUser(status)) {
          throw new Error('Sign-in did not establish a session. Please try again.');
        }
        const user = this.mapStatusToUser(status);
        this.authState.setAuthenticated(user);
        return user;
      })
    );
  }

  private handleLoginError(
    err: unknown,
    email: string,
    context: 'PASSWORD' | 'MFA'
  ): Observable<LoginResponse> {
    this.authState.setLoading(false);

    if (this.isMfaRequiredError(err)) {
      this.authState.setRequiresTwoFactor(email);
      return of({ status: 'MFA_REQUIRED' });
    }

    if (this.isTenantRequiredError(err)) {
      const tenants = this.extractTenants(err);
      this.authState.setTenants(tenants);
      return of({ status: 'TENANT_REQUIRED', tenants });
    }

    if (this.getStatus(err) === 429) {
      this.registerFailedAttempt();
      this.authState.setError('Too many attempts. Please wait a moment and try again.');
      return throwError(() => new Error('Too many attempts'));
    }

    this.registerFailedAttempt();
    const errorMsg =
      context === 'MFA' && this.getStatus(err) === 401
        ? 'That code is not correct. Please check your authenticator app and try again.'
        : this.extractErrorMessage(err, 'Email, password or authentication code is incorrect.');
    this.authState.setError(this.isCurrentlyLockedOut() ? this.lockoutMessage() : errorMsg);
    return throwError(() => new Error(errorMsg));
  }

  private isValidSessionUser(status: AuthStatusResponse): boolean {
    return isValidEmail(status.email ?? '') && !!status.tenant && !!status.role;
  }

  private mapStatusToUser(status: AuthStatusResponse): User {
    const email = normalizeEmail(status.email);
    return {
      id: String(status.tenant_id ?? ''),
      email,
      name: email.split('@')[0],
      tenant: String(status.tenant ?? '').slice(0, 120),
      role: String(status.role ?? '').slice(0, 40)
    };
  }

  private sanitizeMethods(methods: AuthMethods): AuthMethods {
    return {
      email_otp: methods?.email_otp === true,
      password: methods?.password === true,
      microsoft: methods?.microsoft === true,
      email_delivery_configured: methods?.email_delivery_configured === true,
      dev_show_otp: methods?.dev_show_otp === true
    };
  }

  private isMfaRequiredError(err: unknown): boolean {
    return this.getStatus(err) === 401 && this.getDetail(err) === 'MFA_REQUIRED';
  }

  private isTenantRequiredError(err: unknown): boolean {
    if (this.getStatus(err) !== 409) {
      return false;
    }
    return this.getTenantRequiredBody(err)?.reason === 'TENANT_REQUIRED';
  }

  private extractTenants(err: unknown): Tenant[] {
    const body = this.getTenantRequiredBody(err);
    if (!Array.isArray(body?.tenants)) {
      return [];
    }
    return body.tenants
      .map((tenant) => ({
        id: parseTenantId(tenant?.id) ?? 0,
        name: typeof tenant?.name === 'string' ? tenant.name.trim().slice(0, 120) : ''
      }))
      .filter((tenant) => tenant.id > 0 && tenant.name.length > 0);
  }

  private getTenantRequiredBody(
    err: unknown
  ): { reason?: string; tenants?: Tenant[] } | undefined {
    const errorBody = (err as HttpErrorResponse)?.error;
    const detail = this.getDetail(err);
    if (typeof detail === 'object' && detail) {
      return detail;
    }
    if (errorBody && typeof errorBody === 'object' && errorBody.reason === 'TENANT_REQUIRED') {
      return errorBody;
    }
    return undefined;
  }

  private getStatus(err: unknown): number | undefined {
    return (err as HttpErrorResponse)?.status;
  }

  private getDetail(err: unknown): string | { reason?: string; tenants?: Tenant[] } | undefined {
    return (err as { error?: { detail?: string | { reason?: string; tenants?: Tenant[] } } })?.error?.detail;
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const detail = this.getDetail(err);
    if (typeof detail === 'string' && detail !== 'MFA_REQUIRED') {
      return sanitizeUserMessage(detail, fallback);
    }
    return fallback;
  }

  private wipePendingCredentials(): void {
    if (this.pendingCredentials) {
      this.pendingCredentials.password = '';
      this.pendingCredentials.email = '';
    }
    this.pendingCredentials = undefined;
  }

  private registerFailedAttempt(): void {
    this.failedAttempts += 1;
    if (this.failedAttempts >= AUTH_LIMITS.maxFailedAttempts) {
      this.lockoutUntil = Date.now() + AUTH_LIMITS.lockoutSeconds * 1000;
      this.lockoutRemainingSignal.set(AUTH_LIMITS.lockoutSeconds);
      this.startLockoutTicker();
    }
  }

  private resetLockout(): void {
    this.failedAttempts = 0;
    this.lockoutUntil = 0;
    this.lockoutRemainingSignal.set(0);
  }

  private isCurrentlyLockedOut(): boolean {
    if (Date.now() >= this.lockoutUntil) {
      if (this.lockoutUntil) {
        this.resetLockout();
      }
      return false;
    }
    this.lockoutRemainingSignal.set(Math.ceil((this.lockoutUntil - Date.now()) / 1000));
    return true;
  }

  private lockoutMessage(): string {
    return `Too many failed attempts. Try again in ${this.lockoutRemainingSignal() || AUTH_LIMITS.lockoutSeconds} seconds.`;
  }

  private startLockoutTicker(): void {
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((this.lockoutUntil - Date.now()) / 1000));
      this.lockoutRemainingSignal.set(remaining);
      if (remaining > 0) {
        window.setTimeout(tick, 1000);
      } else {
        this.resetLockout();
      }
    };
    window.setTimeout(tick, 1000);
  }

  protected navigateExternal(url: string): void {
    window.location.assign(url);
  }
}
